import { useCallback, useState } from 'react';

import { translationKey, type TranslationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useContributionsPageState } from '#src/app/pages/accountSettings/contributions/main/pageContext';

import { useCancelSubscription } from '../../../../hooks/useCancelSubscription';

type Result = ModelHookResult<
	{
		title: string;
		descriptionHtmlValue: TranslationKey;
		learnMoreLink: string;
		learnMoreLinkDataQa: string;
		descriptionHtmlDataQa: string;
		cancelLabel: string;
		confirmLabel: string;
		confirmDataQa: string;
		cancelDataQa: string;
		isLoading: boolean;
	},
	{
		clickCancelSubscription: (onBeforeCaptcha?: () => void) => Promise<void>;
		stayOnContributionsPage: () => void;
	}
>;

export function useCancelSubscriptionConfirmationModal(): Result {
	const [, { resetPageMessage }] = useContributionsPageState();
	const { translate } = useI18n();
	const cancelSubscription = useCancelSubscription();
	const track = useTracking();
	const [isLoading, setIsLoading] = useState(false);

	const stayOnContributionsPage = useCallback(() => {
		resetPageMessage();
		void track('subs_cancel_modal_stay_click');
	}, [resetPageMessage, track]);

	const clickCancelSubscription = useCallback(
		async (onBeforeCaptcha?: () => void) => {
			resetPageMessage();
			setIsLoading(true);
			try {
				await cancelSubscription({ onBeforeCaptcha });
			} finally {
				setIsLoading(false);
			}
		},
		[cancelSubscription, resetPageMessage],
	);

	return {
		data: {
			title: translate('corgi.membership.manage.cancel.cancel_modal.confirm_cancellation'),
			descriptionHtmlValue: translationKey('corgi.membership.manage.cancel.cancel_modal.cancellation_description'),
			learnMoreLink: '/impact?source_location=cancel_membership_modal',
			learnMoreLinkDataQa: 'learn-more-link',
			descriptionHtmlDataQa: 'cancel-subscription-modal-text',
			confirmLabel: translate('corgi.membership.manage.cancel.cancel_modal.cancel_button'),
			confirmDataQa: 'subscription-cancellation-modal-button-confirm',
			cancelLabel: translate('corgi.membership.manage.cancel.cancel_modal.continue_contributing_button'),
			cancelDataQa: 'subscription-cancellation-modal-button-cancel',
			isLoading,
		},
		actions: {
			clickCancelSubscription,
			stayOnContributionsPage,
		},
	};
}
